/**
 * Product card / search result "Add to Cart" — port of Innovist's
 * custom-cart.js card flow.
 *
 * The button calls /cart/add.js itself instead of submitting a product form:
 * the Shiprocket side cart hooks form submits, which left Horizon's form with
 * no busy state and a stale header count. While adding, the button shows the
 * PDP loader (aria-busy, styled in theme-custom.css); once the variant is in
 * the cart the button becomes a − qty + stepper, the header <cart-icon> is
 * updated and Shiprocket is asked to refresh. Every card for the same variant
 * mirrors the real cart, including changes made in the Shiprocket drawer or
 * the PDP sticky bar.
 */
(() => {
  const CARD = '[data-ac-card-cart]';
  const MIN_BUSY_MS = 250; // don't let a fast add flash the loader
  /** @type {Map<string, {qty: number, key: string | null, busy: boolean}>} */
  const state = new Map();
  let toastEl = null;
  let toastTimer = null;
  let syncTimer = null;

  const variantState = (id) => {
    if (!state.has(id)) state.set(id, { qty: 0, key: null, busy: false });
    return state.get(id);
  };
  const cardsFor = (id) => document.querySelectorAll(`${CARD}[data-variant-id="${id}"]`);

  /* ---- rendering ---- */
  function renderCard(card) {
    const s = state.get(card.dataset.variantId);
    const qty = s ? s.qty : 0;
    const button = card.querySelector('.ac-atc');
    const stepper = card.querySelector('.ac-card-qty');
    if (!button || !stepper) return;
    const qtyEl = stepper.querySelector('.qty');
    if (qtyEl && qty > 0) qtyEl.textContent = String(qty);
    button.hidden = qty > 0;
    stepper.hidden = qty <= 0;
  }
  const renderVariant = (id) => cardsFor(id).forEach(renderCard);

  function setBusy(id, busy) {
    variantState(id).busy = busy;
    cardsFor(id).forEach((card) => {
      const button = card.querySelector('.ac-atc');
      const stepper = card.querySelector('.ac-card-qty');
      if (stepper && !stepper.hidden) {
        stepper.classList.toggle('is-loading', busy);
        stepper.querySelectorAll('button').forEach((b) => { b.disabled = busy; });
      }
      if (button) {
        button.disabled = busy;
        if (busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      }
    });
  }

  /* ---- header count: Horizon <cart-icon> ---- */
  function setHeaderCount(count, animate) {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, animate);
    });
  }
  function bumpHeaderCount(delta) {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble !== 'function') return;
      const current = Number(icon.currentCartCount) || 0;
      icon.renderCartBubble(Math.max(0, current + delta), delta > 0);
    });
  }

  /* ---- "added to cart" toast (Innovist .CartNotifaction-Sec) ---- */
  function showToast(text) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ac-cart-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.innerHTML =
        '<span class="ac-cart-toast__content"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M18.4053 14.9199L10.6787 19.167V10.8359L13.5176 9.27441V12.8789L15.542 11.7412V8.1377L18.4053 6.58301V14.9199ZM9.33105 10.8203V19.1182L1.59375 14.9297V6.57812L9.33105 10.8203ZM12.8027 7.94336L9.97461 9.55469L2.23633 5.19141L5.09473 3.59473L12.8027 7.94336ZM17.7051 5.18164L14.877 6.79395L7.13867 2.42969L9.99707 0.833984L17.7051 5.18164Z" fill="#0A0A0A"/></svg><span class="ac-cart-toast__text"></span></span>';
      document.body.appendChild(toastEl);
    }
    toastEl.querySelector('.ac-cart-toast__text').textContent = text;
    toastEl.classList.remove('is-error');
    toastEl.classList.add('active');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('active'), 3000);
  }
  function showError(text) {
    showToast(text);
    toastEl.classList.add('is-error');
  }
  function hideToast() {
    if (!toastEl) return;
    toastEl.classList.remove('active');
    clearTimeout(toastTimer);
  }

  /* ---- cart requests ---- */
  async function cartPost(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) return res.json();
    const json = await res.json().catch(() => ({}));
    throw new Error(json.description || json.message || 'Could not update cart. Please try again.');
  }

  function applyCart(cart) {
    const seen = new Set();
    // B1G1 can split a variant over lines: sum them, and step the biggest line.
    (cart.items || []).forEach((item) => {
      const id = String(item.variant_id);
      const s = variantState(id);
      if (!seen.has(id)) {
        seen.add(id);
        s.qty = 0;
        s.key = null;
        s.keyQty = 0;
      }
      s.qty += item.quantity;
      if (item.quantity > (s.keyQty || 0)) {
        s.key = item.key;
        s.keyQty = item.quantity;
      }
    });
    state.forEach((s, id) => {
      if (!seen.has(id)) {
        s.qty = 0;
        s.key = null;
      }
    });
    state.forEach((s, id) => { if (!s.busy) renderVariant(id); });
    return cart;
  }

  async function syncFromCart(updateHeader) {
    if (!document.querySelector(CARD)) return;
    const res = await fetch(`/cart.js?ts=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    const cart = await res.json();
    applyCart(cart);
    if (updateHeader) setHeaderCount(cart.item_count, false);
  }
  function scheduleSync(delay, updateHeader) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => syncFromCart(updateHeader).catch(() => {}), delay);
  }

  function afterMutation() {
    window.postMessage({ name: 'SRSC_UPDATE_CART', body: {} }, '*');
    scheduleSync(150, true);
  }

  function pushAddToCartEvent(item, card) {
    if (!item) return;
    const price = (item.final_price || item.price || 0) / 100;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'Custom_addtocart',
      ecommerce: {
        page_location: window.location.href,
        page_title: document.title,
        click_from: card.closest('.ac-result') ? 'search_results' : 'product_card',
        value: price,
        currency: 'INR',
        items: [{
          item_id: `shopify_IN_${item.product_id}_${item.variant_id}`,
          item_name: item.product_title,
          item_brand: item.vendor,
          item_category: item.product_type || 'null',
          price,
          quantity: 1,
          item_variant: item.variant_title || 'null',
        }],
      },
    });
  }

  async function addOne(id, card) {
    const s = variantState(id);
    if (s.busy) return;
    const started = performance.now();
    setBusy(id, true);
    try {
      const item = await cartPost('/cart/add.js', { id: Number(id), quantity: 1 });
      if (item && item.key) {
        s.key = item.key;
        s.keyQty = item.quantity;
      }
      s.qty += 1;
      await new Promise((r) => setTimeout(r, Math.max(0, MIN_BUSY_MS - (performance.now() - started))));
      setBusy(id, false);
      renderVariant(id);
      bumpHeaderCount(1);
      showToast('Product has been added to cart');
      pushAddToCartEvent(item, card);
      afterMutation();
    } catch (error) {
      setBusy(id, false);
      showError(error.message);
      scheduleSync(0, true);
    }
  }

  async function removeOne(id) {
    const s = variantState(id);
    if (s.busy || !s.key || s.qty <= 0) return;
    setBusy(id, true);
    const keyQty = s.keyQty || s.qty;
    try {
      const cart = await cartPost('/cart/change.js', { id: s.key, quantity: Math.max(0, keyQty - 1) });
      setBusy(id, false);
      applyCart(cart);
      setHeaderCount(cart.item_count, false);
      hideToast();
      afterMutation();
    } catch (error) {
      setBusy(id, false);
      showError(error.message);
      scheduleSync(0, true);
    }
  }

  /* ---- events ---- */
  document.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    const card = target.closest && target.closest(CARD);
    if (!card) return;
    const id = card.dataset.variantId;
    if (!id) return;
    if (target.closest('.increase-qty')) {
      event.preventDefault();
      addOne(id, card);
    } else if (target.closest('.decrease-qty')) {
      event.preventDefault();
      removeOne(id);
    } else if (target.closest('button.ac-atc')) {
      event.preventDefault();
      addOne(id, card);
    }
  });

  // Cart changes made elsewhere: Shiprocket drawer, PDP sticky bar.
  window.addEventListener('message', (event) => {
    const name = event.data && event.data.name;
    if (['quantity_change', 'item_added', 'item_removed', 'cart_update'].includes(name)) scheduleSync(0, false);
  });
  document.addEventListener('ac:cart-changed', () => scheduleSync(0, false));
  window.addEventListener('pageshow', (event) => { if (event.persisted) scheduleSync(0, false); });

  // Cards rendered later (collection filters, sliders, recently viewed, search).
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches(CARD) || el.querySelector(CARD)) {
          if (state.size) el.querySelectorAll(CARD).forEach(renderCard);
          if (el.matches(CARD)) renderCard(el);
          scheduleSync(300, false);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  scheduleSync(0, false);
})();
